import React, { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import MaterialHeatmapImproved from "./components/MaterialHeatmapImproved";
import MaterialBarChartHorizontal from "./components/MaterialBarChartHorizontal";
import MaterialPieChart from "./components/MaterialPieChart";
import MaterialComparisonChart from "./components/MaterialComparisonChart";
import RadarChart from "./components/RadarChart";
import AnalyticalLoadingScreen from "./components/AnalyticalLoadingScreen";
import {
  Upload,
  FileText,
  BarChart3,
  Activity,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  XCircle,
  Download,
  Eye,
  Brain,
  Target,
  Database,
  Sparkles,
  RefreshCw,
  Layers,
  GitBranch,
  Shuffle,
  BarChart2,
  Settings,
  Table,
  Search,
  ArrowUpDown,
  Lightbulb,
} from "lucide-react";
import "./App.css";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://www.lelalign.org";

function App() {
  const [files, setFiles] = useState({ master: null, ies: null });
  const [uploadStatus, setUploadStatus] = useState({ master: 0, ies: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("upload");
  const [processingStep, setProcessingStep] = useState("");
  const [animationKey, setAnimationKey] = useState(0);

  // New state for enhanced functionality
  const [selectedProfile, setSelectedProfile] = useState("semantico");
  const [availableProfiles, setAvailableProfiles] = useState({});
  const [currentProfileInfo, setCurrentProfileInfo] = useState(null);
  const [topMatches, setTopMatches] = useState([]);
  const [alignedTerms, setAlignedTerms] = useState([]);
  const [missingTerms, setMissingTerms] = useState([]);
  const [finalStates, setFinalStates] = useState({});
  const [termDetail, setTermDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [selectedComparisonIndex, setSelectedComparisonIndex] = useState(0);
  const [filterState, setFilterState] = useState("all");
  const [sortBy, setSortBy] = useState("similarity");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [embedingsWarning, setEmbeddingsWarning] = useState(false);
  const [fileSummary, setFileSummary] = useState(null);
  const [comparisonDetails, setComparisonDetails] = useState([]);
  const [componentPercentages, setComponentPercentages] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  const lexicalSimilarityPercentage =
    statistics?.lexical_similarity_percentage ||
    (statistics?.avg_lexical_similarity ?? 0) * 100;
  const syntacticSimilarityPercentage =
    statistics?.syntactic_similarity_percentage ||
    (statistics?.avg_syntactic_similarity ?? 0) * 100;
  const semanticSimilarityPercentage =
    statistics?.semantic_similarity_percentage ||
    (statistics?.avg_semantic_similarity ?? 0) * 100;

  const groupedAlignedTerms = useMemo(() => {
    const groups = new Map();

    alignedTerms.forEach(({ ies_term, master_term }) => {
      if (!ies_term) return;
      const trimmedIes = ies_term.trim();
      if (!groups.has(trimmedIes)) {
        groups.set(trimmedIes, new Set());
      }
      if (master_term) {
        groups.get(trimmedIes).add(master_term.trim());
      }
    });

    return Array.from(groups.entries()).map(([iesTerm, masters]) => ({
      ies_term: iesTerm,
      master_terms: Array.from(masters).filter(Boolean),
    }));
  }, [alignedTerms]);

  const groupedDifferenceTerms = useMemo(() => {
    // Filtrar términos que tienen estado "Diferencia" en finalStates
    return Object.keys(finalStates || {}).filter(term => finalStates[term] === "Diferencia");
  }, [finalStates]);

  useEffect(() => {
    setAnimationKey((prev) => prev + 1);
    loadAvailableProfiles();
  }, [activeTab]);

  const loadAvailableProfiles = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/profiles`);
      if (response.data.status === "success") {
        setAvailableProfiles(response.data.profiles);
        setCurrentProfileInfo({
          name: response.data.current_profile,
          ...response.data.profiles[response.data.current_profile],
        });
        setSelectedProfile(response.data.current_profile);
        setEmbeddingsWarning(!response.data.embeddings_available);
      }
    } catch (err) {
      console.error("Error loading profiles:", err);
    }
  };

  const handleProfileChange = async (profileName) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/set-profile/${profileName}`
      );
      if (response.data.status === "success") {
        setSelectedProfile(profileName);
        setCurrentProfileInfo({
          name: profileName,
          weights: response.data.weights,
          thresholds: response.data.thresholds,
        });
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cambiar perfil");
    }
  };

  const onDropMaster = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file && file.name.endsWith(".csv")) {
      setFiles((prev) => ({ ...prev, master: file }));
      setUploadStatus((prev) => ({ ...prev, master: file.size }));
      setError(null);
    } else {
      setError("Por favor selecciona un archivo CSV válido para Dominio Maestro");
    }
  }, []);

  const onDropIES = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file && file.name.endsWith(".csv")) {
      setFiles((prev) => ({ ...prev, ies: file }));
      setUploadStatus((prev) => ({ ...prev, ies: file.size }));
      setError(null);
    } else {
      setError("Por favor selecciona un archivo CSV válido para Dominio IES");
    }
  }, []);

  const {
    getRootProps: getMasterRootProps,
    getInputProps: getMasterInputProps,
    isDragActive: isMasterDragActive,
  } = useDropzone({
    onDrop: onDropMaster,
    accept: { "text/csv": [".csv"] },
    multiple: false,
  });

  const {
    getRootProps: getIESRootProps,
    getInputProps: getIESInputProps,
    isDragActive: isIESDragActive,
  } = useDropzone({
    onDrop: onDropIES,
    accept: { "text/csv": [".csv"] },
    multiple: false,
  });

  const uploadFiles = async () => {
    if (!files.master || !files.ies) {
      setError("Por favor selecciona ambos archivos antes de continuar");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProcessingStep("Subiendo archivos...");

    try {
      const formData = new FormData();
      formData.append("master_file", files.master);
      formData.append("ies_file", files.ies);

      const uploadResponse = await axios.post(
        `${API_BASE_URL}/api/upload-files`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (uploadResponse.data.status === "success") {
        setFileSummary({
          masterSymbols: uploadResponse.data.master_symbols || 0,
          iesSymbols: uploadResponse.data.ies_symbols || 0,
        });
        setProcessingStep("Preprocesamiento texto.");
        await new Promise((resolve) => setTimeout(resolve, 1000));

        setProcessingStep("Realizando comparación.");
        await new Promise((resolve) => setTimeout(resolve, 1000));

        setProcessingStep("Realizando comparación.");
        await new Promise((resolve) => setTimeout(resolve, 1000));

        setProcessingStep("Realizando comparación.");

        const processResponse = await axios.post(
          `${API_BASE_URL}/api/process-similarity`
        );

        if (processResponse.data.status === "success") {
          setProcessingStep("Generando resultados de la alineación...");
          await new Promise((resolve) => setTimeout(resolve, 500));

          const heatmapData =
            processResponse.data.visualization_data?.heatmap_data;
          if (heatmapData) {
            setResults(processResponse.data.visualization_data);
            setStatistics(processResponse.data.statistics);
            setCurrentProfileInfo(processResponse.data.profile_info);
            setFinalStates(processResponse.data.final_states);
            setTopMatches(processResponse.data.top_matches);
            setAlignedTerms(processResponse.data.aligned_terms);
            setMissingTerms(processResponse.data.missing_terms);
            setComparisonDetails(processResponse.data.comparison_details || []);
            setComponentPercentages(
              processResponse.data.statistics?.component_percentages || null
            );
            setActiveTab("results");
            setProcessingStep("");
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error al procesar los archivos");
      setProcessingStep("");
    } finally {
      setIsProcessing(false);
    }
  };

  const resetAnalysis = () => {
    setFiles({ master: null, ies: null });
    setUploadStatus({ master: 0, ies: 0 });
    setResults(null);
    setStatistics(null);
    setError(null);
    setActiveTab("upload");
    setProcessingStep("");
    setTopMatches([]);
    setAlignedTerms([]);
    setMissingTerms([]);
    setFinalStates({});
    setTermDetail(null);
    setShowDetailModal(false);
    setFileSummary(null);
    setComparisonDetails([]);
    setComponentPercentages(null);
    setIsProcessing(false);
  };

  const handleTermClick = async (iesTermName) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/term-detail/${encodeURIComponent(iesTermName)}`
      );
      if (response.data.status === "success") {
        setTermDetail(response.data);
        setSelectedComparisonIndex(0);
        setShowDetailModal(true);
      }
    } catch (err) {
      setError("Error al cargar detalles del término");
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/export-data`);
      if (response.data.status === "success") {
        // Convert to CSV
        const csvData = response.data.export_data;
        if (csvData.length === 0) return;

        const headers = Object.keys(csvData[0]);
        const csvContent = [
          headers.join(";"),
          ...csvData.map((row) =>
            headers.map((header) => `"${row[header] || ""}"`).join(";")
          ),
        ].join("\n");

        // Download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `LEL_Analysis_${selectedProfile}_${
          new Date().toISOString().split("T")[0]
        }.csv`;
        link.click();
      }
    } catch (err) {
      setError("Error al exportar datos");
    }
  };

  const handleGeneratePdf = async () => {
    if (!topMatches || topMatches.length === 0) return;
    setLoadingPdf(true);
    try {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const now = new Date();
      const fechaStr = now.toLocaleString("es-EC", {
        timeZone: "America/Guayaquil",
      });
      const fechaArchivo = now.toISOString().split("T")[0];

      // ── Header institucional ──
      const HEADER_H = 26;
      const drawHeader = () => {
        doc.setFillColor(12, 74, 110); // navy
        doc.rect(0, 0, pageW, HEADER_H, "F");
        doc.setFontSize(14);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.text("LEL NATURAL — REPORTE DE ANÁLISIS DE SIMILITUD", pageW / 2, 11, {
          align: "center",
        });
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(220, 230, 240);
        doc.text(
          "Análisis de glosarios de dominio mediante NLP multi-métrica",
          pageW / 2,
          17,
          { align: "center" }
        );
        doc.setFontSize(7.5);
        doc.text(`Generado: ${fechaStr}`, pageW / 2, 22, { align: "center" });
      };
      drawHeader();

      const margin = 10;

      // ── Distribución de estados (barra apilada) ──
      const counts = {
        similitud: Object.values(finalStates).filter((s) => s === "Similitud").length,
        diferencia: Object.values(finalStates).filter((s) => s === "Diferencia").length,
        omision: Object.values(finalStates).filter((s) => s === "Omisión").length,
        total: Object.keys(finalStates).length || 1,
      };

      const distY = HEADER_H + 6;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text(
        `Distribución de estados (n = ${Object.keys(finalStates).length})`,
        margin,
        distY
      );

      const barX = margin;
      const barY = distY + 3;
      const barW = pageW - margin * 2;
      const barH = 7;

      const segments = [
        { key: "similitud", label: "Similitud", color: [22, 163, 74], value: counts.similitud },
        { key: "diferencia", label: "Diferencia", color: [202, 138, 4], value: counts.diferencia },
        { key: "omision", label: "Omisión", color: [185, 28, 28], value: counts.omision },
      ];

      // Fondo gris claro para la barra
      doc.setFillColor(235, 238, 242);
      doc.rect(barX, barY, barW, barH, "F");

      let cursorX = barX;
      segments.forEach((seg) => {
        const segW = (seg.value / counts.total) * barW;
        if (segW > 0) {
          doc.setFillColor(seg.color[0], seg.color[1], seg.color[2]);
          doc.rect(cursorX, barY, segW, barH, "F");
          cursorX += segW;
        }
      });
      // Borde sutil
      doc.setDrawColor(200, 200, 200);
      doc.rect(barX, barY, barW, barH, "S");

      // Leyenda debajo
      const legendY = barY + barH + 6;
      const legendItemW = barW / segments.length;
      segments.forEach((seg, i) => {
        const lx = barX + i * legendItemW;
        doc.setFillColor(seg.color[0], seg.color[1], seg.color[2]);
        doc.rect(lx, legendY - 3, 3, 3, "F");
        const pct = ((seg.value / counts.total) * 100).toFixed(1);
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(40, 40, 40);
        doc.text(seg.label, lx + 5, legendY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(90, 90, 90);
        doc.text(`${seg.value} (${pct}%)`, lx + 5, legendY + 4);
      });

      // ── Configuración del análisis ──
      const profile = currentProfileInfo || {};
      const thresholds = profile.thresholds || {};
      const configY = legendY + 10;
      autoTable(doc, {
        startY: configY,
        head: [["Parámetro", "Valor"]],
        body: [
          ["Perfil de matching", selectedProfile || "—"],
          [
            "Umbral de similitud",
            thresholds.similarity != null ? `${(thresholds.similarity * 100).toFixed(0)}%` : "—",
          ],
          [
            "Umbral de diferencia",
            thresholds.difference != null ? `${(thresholds.difference * 100).toFixed(0)}%` : "—",
          ],
          ["Símbolos dominio Maestro", String(statistics?.master_symbols ?? "—")],
          ["Símbolos dominio IES", String(statistics?.ies_symbols ?? "—")],
          [
            "Similitud promedio",
            statistics?.average_similarity != null
              ? `${(statistics.average_similarity * 100).toFixed(2)}%`
              : "—",
          ],
          [
            "Porcentaje de alineación",
            statistics?.alignment_percentage != null
              ? `${statistics.alignment_percentage.toFixed(1)}%`
              : "—",
          ],
        ],
        theme: "grid",
        headStyles: {
          fillColor: [12, 74, 110],
          textColor: 255,
          fontSize: 9,
          fontStyle: "bold",
        },
        bodyStyles: { fontSize: 8.5, textColor: [50, 50, 50] },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 70 } },
        margin: { left: margin, right: margin },
      });

      // ── Top matches (flujo continuo) ──
      const topY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 30, 30);
      doc.text("Mejor coincidencia por término IES", margin, topY);

      autoTable(doc, {
        startY: topY + 2,
        head: [["Término IES", "Mejor coincidencia Maestro", "Puntaje", "Estado"]],
        body: topMatches.map((m) => [
          m.ies_term || "—",
          m.master_term || "—",
          `${(m.similarity * 100).toFixed(1)}%`,
          m.status || "—",
        ]),
        theme: "striped",
        headStyles: {
          fillColor: [12, 74, 110],
          textColor: 255,
          fontSize: 9,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          2: { halign: "right" },
          3: { halign: "center" },
        },
        didParseCell: (hookData) => {
          if (hookData.section === "body" && hookData.column.index === 3) {
            const v = String(hookData.cell.raw);
            if (v === "Similitud") {
              hookData.cell.styles.fillColor = [220, 252, 231];
              hookData.cell.styles.textColor = [22, 101, 52];
              hookData.cell.styles.fontStyle = "bold";
            } else if (v === "Diferencia") {
              hookData.cell.styles.fillColor = [254, 249, 195];
              hookData.cell.styles.textColor = [133, 77, 14];
              hookData.cell.styles.fontStyle = "bold";
            } else if (v === "Omisión") {
              hookData.cell.styles.fillColor = [254, 226, 226];
              hookData.cell.styles.textColor = [153, 27, 27];
              hookData.cell.styles.fontStyle = "bold";
            }
          }
        },
        margin: { left: margin, right: margin },
      });

      // ── Listas (flujo continuo, autoTable maneja saltos de página) ──
      const renderList = (titulo, items, tono) => {
        let y = doc.lastAutoTable
          ? doc.lastAutoTable.finalY + 8
          : HEADER_H + 10;

        // Si no cabe el título cómodamente, salta de página
        if (y > pageH - 30) {
          doc.addPage();
          drawHeader();
          y = HEADER_H + 10;
        }

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 30, 30);
        doc.text(`${titulo} (${items.length})`, margin, y);

        if (items.length === 0) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "italic");
          doc.setTextColor(120, 120, 120);
          doc.text("Sin registros.", margin, y + 6);
          // Forzar que lastAutoTable.finalY refleje esta posición
          doc.lastAutoTable = { finalY: y + 6 };
          return;
        }

        autoTable(doc, {
          startY: y + 2,
          head: [["#", "Término"]],
          body: items.map((t, i) => [String(i + 1), t]),
          theme: "striped",
          headStyles: {
            fillColor: tono.head,
            textColor: 255,
            fontSize: 9,
            fontStyle: "bold",
          },
          bodyStyles: { fontSize: 8.5 },
          alternateRowStyles: { fillColor: tono.alt },
          columnStyles: {
            0: { halign: "center", cellWidth: 14, fontStyle: "bold" },
          },
          margin: { left: margin, right: margin },
        });
      };

      const alineadosList = groupedAlignedTerms.map((e) => {
        const masters = e.master_terms?.length ? ` ↔ ${e.master_terms.join(", ")}` : "";
        return `${e.ies_term}${masters}`;
      });
      const diferenciasList = groupedDifferenceTerms;
      const faltantesList = missingTerms.map((t) => {
        const isMaster =
          t.domain === "Master" ||
          (t.master_term && t.master_term !== "N/A" && t.ies_term === "N/A");
        const name = isMaster ? t.master_term : t.ies_term;
        const dom = isMaster ? "Master" : "IES";
        return `[${dom}] ${name}  (sim. máx.: ${(t.similarity * 100).toFixed(1)}%)`;
      });
      const omisionesList = Object.entries(finalStates)
        .filter(([, s]) => s === "Omisión")
        .map(([term]) => term);

      renderList("Términos alineados", alineadosList, {
        head: [16, 130, 60],
        alt: [236, 253, 245],
      });
      renderList("Términos con diferencias", diferenciasList, {
        head: [180, 130, 20],
        alt: [254, 249, 231],
      });
      renderList("Términos faltantes", faltantesList, {
        head: [180, 30, 30],
        alt: [254, 242, 242],
      });
      renderList("Símbolos con omisiones", omisionesList, {
        head: [153, 27, 27],
        alt: [254, 226, 226],
      });

      // ── Footer paginado ──
      const totalPages = doc.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.setFont("helvetica", "normal");
        doc.text(
          "LEL Natural — Reporte académico de análisis de similitud de glosarios",
          margin,
          pageH - 5
        );
        doc.text(`Página ${p} de ${totalPages}`, pageW - margin, pageH - 5, {
          align: "right",
        });
      }

      doc.save(`LEL_Reporte_${selectedProfile || "analisis"}_${fechaArchivo}.pdf`);
    } catch (err) {
      console.error("Error generando PDF:", err);
      setError("Error al generar el PDF. Revisa la consola.");
    } finally {
      setLoadingPdf(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
    trend,
    isAnimated = false,
  }) => (
    <div
      className={`bg-white rounded-xl p-4 card-shadow card-hover border-l-4 ${color} ${
        isAnimated ? "glow-effect" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-xs font-medium">{title}</p>
          <p className="text-xl font-bold text-gray-900 mt-1 transition-all duration-500">
            {value}
          </p>
          {subtitle && <p className="text-gray-500 text-xs mt-1">{subtitle}</p>}
        </div>
        <div
          className={`p-2 rounded-full ${color
            .replace("border-l-", "bg-")
            .replace("-500", "-100")} floating`}
        >
          <Icon
            className={`w-4 h-4 ${color
              .replace("border-l-", "text-")
              .replace("-500", "-600")}`}
          />
        </div>
      </div>
      {trend && (
        <div className="mt-2 flex items-center">
          <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
          <span className="text-green-500 text-xs font-medium">{trend}</span>
        </div>
      )}
    </div>
  );

  const TabButton = ({
    id,
    label,
    icon: Icon,
    isActive,
    onClick,
    disabled = false,
  }) => (
    <button
      onClick={() => !disabled && onClick(id)}
      disabled={disabled}
      className={`flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 transform ${
        isActive
          ? "bg-white text-gray-800 shadow-lg scale-105 border border-gray-200"
          : disabled
          ? "text-white/40 cursor-not-allowed opacity-50"
          : "text-white/90 hover:bg-white/15 hover:text-white hover:scale-105 hover:shadow-md"
      }`}
    >
      <Icon className="w-4 h-4 mr-2" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );

  // Filter and sort top matches
  const getFilteredTopMatches = () => {
    if (!topMatches) return [];

    let filtered = topMatches.filter((match) => {
      const matchesFilter =
        filterState === "all" || match.status === filterState;
      const matchesSearch =
        searchTerm === "" ||
        match.ies_term.toLowerCase().includes(searchTerm.toLowerCase()) ||
        match.master_term.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesSearch;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "similarity") {
        aVal = a.similarity;
        bVal = b.similarity;
      } else if (sortBy === "ies_term") {
        aVal = a.ies_term;
        bVal = b.ies_term;
      } else if (sortBy === "master_term") {
        aVal = a.master_term;
        bVal = b.master_term;
      } else if (sortBy === "status") {
        aVal = a.status;
        bVal = b.status;
      }

      if (typeof aVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      } else {
        return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
      }
    });

    return filtered;
  };

  const getStateColor = (state) => {
    switch (state) {
      case "Similitud":
        return "bg-[#22c55e]/80 text-white";
      case "Diferencia":
        return "bg-[#fbbf24]/80 text-white";
      case "Omisión":
        return "bg-[#ef4444]/80 text-white";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const formatScore = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) return "--";
    return `${(value * 100).toFixed(1)}%`;
  };

  const ProfileChip = ({ label, value, className = "" }) => (
    <div
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm bg-white/20 border border-white/30 ${className}`}
    >
      <span className="font-medium">{label}:</span>
      <span className="ml-1">{value}</span>
    </div>
  );

  const ThresholdBar = ({ thresholds }) => {
    const difference = thresholds.difference;
    const similarity = thresholds.similarity;

    return (
      <div className="w-full">
        <h4 className="text-sm font-bold text-gray-900 mb-4 text-center">Umbrales</h4>
        <div className="relative w-full h-8 bg-gray-200 rounded-full overflow-visible mb-6">
          {/* Rojo: Diferencia */}
          <div
            className="absolute left-0 top-0 h-full bg-red-500"
            style={{ width: `${difference * 100}%` }}
          ></div>
          {/* Amarillo: Neutral */}
          <div
            className="absolute top-0 h-full bg-yellow-500"
            style={{ left: `${difference * 100}%`, width: `${(similarity - difference) * 100}%` }}
          ></div>
          {/* Verde: Similitud */}
          <div
            className="absolute top-0 h-full bg-green-500"
            style={{ left: `${similarity * 100}%`, width: `${(1 - similarity) * 100}%` }}
          ></div>
          {/* Número en difference */}
          <div
            className="absolute top-[-25px] transform -translate-x-1/2"
            style={{ left: `${difference * 100}%` }}
          >
            <div className="text-xs font-semibold text-gray-800">{difference.toFixed(2)}</div>
          </div>
          {/* Flecha en difference */}
          <div
            className="absolute bg-black"
            style={{ left: `${difference * 100}%`, top: '-10px', width: '2px', height: '10px', transform: 'translateX(-50%)' }}
          ></div>
          {/* Número en similarity */}
          <div
            className="absolute top-[-25px] transform -translate-x-1/2"
            style={{ left: `${similarity * 100}%` }}
          >
            <div className="text-xs font-semibold text-gray-800">{similarity.toFixed(2)}</div>
          </div>
          {/* Flecha en similarity */}
          <div
            className="absolute bg-black"
            style={{ left: `${similarity * 100}%`, top: '-10px', width: '1px', height: '10px', transform: 'translateX(-50%)' }}
          ></div>
        </div>
        <div className="flex justify-between text-xs text-gray-600">
          <span>Diferencia</span>
          <span>En zona neutral</span>
          <span>Similitud</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-petrol-900">
      {/* Header - Sistema Estandarizado */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="slide-in">
              <h1 className="text-2xl sm:text-3xl font-bold gradient-text truncate">
                Alineación de glosarios LEL
              </h1>
              <p className="text-white/70 mt-1 text-sm sm:text-base truncate">
                Análisis de similitud entre glosarios
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="bg-green-500/20 text-green-300 px-3 py-1.5 rounded-full text-xs border border-green-500/30 flex items-center gap-2">
                <CheckCircle className="w-3 h-3" />
                <span className="truncate">Sistema Activo</span>
              </div>
              {(results || statistics) && (
                <button
                  onClick={resetAnalysis}
                  className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full text-xs transition-all duration-200 border border-white/30 flex items-center gap-2"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span className="hidden sm:inline">Nuevo Análisis</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs - Sistema Estandarizado */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-wrap gap-2 bg-white/10 backdrop-blur-lg rounded-lg p-3 border border-white/20 shadow-md">
          <TabButton
            id="upload"
            label="📁 Subir Archivos"
            icon={Upload}
            isActive={activeTab === "upload"}
            onClick={setActiveTab}
          />
          <TabButton
            id="results"
            label="📊 Resultados"
            icon={BarChart3}
            isActive={activeTab === "results"}
            onClick={setActiveTab}
            disabled={!results}
          />
          <TabButton
            id="analysis"
            label="🔍 Análisis Detallado"
            icon={Table}
            isActive={activeTab === "analysis"}
            onClick={setActiveTab}
            disabled={!topMatches || topMatches.length === 0}
          />
          <TabButton
            id="statistics"
            label="📈 Estadísticas"
            icon={Activity}
            isActive={activeTab === "statistics"}
            onClick={setActiveTab}
            disabled={!statistics}
          />
        </div>
      </div>

      {/* Main Content - Sistema Estandarizado */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {embedingsWarning && (
          <div className="mb-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 fade-in backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <p className="text-yellow-200 text-sm">
                Advertencia: sentence-transformers no está disponible. Instala
                con: pip install sentence-transformers y reinicia el servidor.
              </p>
            </div>
          </div>
        )}

        {/* Upload Tab - Sistema Estandarizado */}
        {activeTab === "upload" && (
          <div key={animationKey} className="space-y-6 fade-in">
            {/* Profile Selection and Thresholds */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 card-shadow border border-white/20">
                <div className="flex items-center mb-4 gap-3">
                  <Settings className="w-5 h-5 text-navy-600 flex-shrink-0" />
                  <h3 className="text-lg font-bold text-gray-900 truncate">
                    Selección de Perfil de Similitud
                  </h3>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Perfil de Similitud
                  </label>
                  <select
                    id="profile-select"
                    value={selectedProfile}
                    onChange={(e) => handleProfileChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 text-sm"
                  >
                    {Object.keys(availableProfiles).map((profile) => (
                      <option key={profile} value={profile}>
                        {profile.charAt(0).toUpperCase() + profile.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {currentProfileInfo && (
                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 card-shadow border border-white/20">
                  <div className="flex items-center mb-4 gap-3">
                    <BarChart3 className="w-5 h-5 text-navy-600 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900 truncate">
                      Umbrales Activos
                    </h3>
                  </div>
                  <ThresholdBar thresholds={currentProfileInfo.thresholds} />
                </div>
              )}
            </div>

            {!isProcessing ? (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6 floating">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">
                  Carga de Glosarios
                </h2>
                <p className="text-white/70 max-w-3xl mx-auto text-lg leading-relaxed mb-4">
                  Sube tus archivos CSV con los glosarios de dominios para
                  realizar el análisis de similitud avanzado.
                </p>
                <button
                  onClick={() => setShowFormatModal(true)}
                  className="inline-flex items-center gap-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-all duration-200 border border-white/20 hover:border-white/30"
                >
                  <AlertCircle className="w-4 h-4" />
                  ¿Qué formato usar?
                </button>
              </div>
            ) : (
              <AnalyticalLoadingScreen
                processingStep={processingStep}
                isProcessing={isProcessing}
              />
            )}

            {!isProcessing && (
              <>
                {error && (
                  <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-5 fade-in backdrop-blur-sm shadow-lg">
                    <div className="flex items-center gap-3">
                      <XCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                      <p className="text-red-200 font-medium">{error}</p>
                    </div>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Master Domain Upload */}
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 card-shadow card-hover border border-white/20">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-navy-500 to-navy-600 rounded-full mb-4 floating">
                        <FileText className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Dominio Maestro
                      </h3>
                      <p className="text-gray-600 mt-2">
                        Archivo principal de referencia
                      </p>
                    </div>

                    <div
                      {...getMasterRootProps()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
                        isMasterDragActive
                          ? "border-navy-500 bg-navy-50 scale-105 glow-effect"
                          : files.master
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 hover:border-navy-400 hover:bg-gray-50 hover:scale-102"
                      }`}
                    >
                      <input {...getMasterInputProps()} />
                      {files.master ? (
                        <div className="space-y-3 fade-in">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto animate-bounce" />
                          <p className="text-green-700 font-bold text-lg">
                            {files.master.name}
                          </p>
                          <p className="text-gray-500">
                            {formatBytes(uploadStatus.master)}
                          </p>
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm inline-block">
                            ✓ Archivo validado
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                          <p className="text-gray-600 font-medium">
                            {isMasterDragActive
                              ? "Suelta el archivo aquí"
                              : "Arrastra tu archivo CSV o haz clic para seleccionar"}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Formatos soportados: CSV
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* IES Domain Upload */}
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 card-shadow card-hover border border-white/20">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-petrol-500 to-petrol-600 rounded-full mb-4 floating">
                        <Database className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Dominio IES
                      </h3>
                      <p className="text-gray-600 mt-2">Archivo a comparar</p>
                    </div>

                    <div
                      {...getIESRootProps()}
                      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
                        isIESDragActive
                          ? "border-petrol-500 bg-petrol-50 scale-105 glow-effect"
                          : files.ies
                          ? "border-green-500 bg-green-50"
                          : "border-gray-300 hover:border-petrol-400 hover:bg-gray-50 hover:scale-102"
                      }`}
                    >
                      <input {...getIESInputProps()} />
                      {files.ies ? (
                        <div className="space-y-3 fade-in">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto animate-bounce" />
                          <p className="text-green-700 font-bold text-lg">
                            {files.ies.name}
                          </p>
                          <p className="text-gray-500">
                            {formatBytes(uploadStatus.ies)}
                          </p>
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm inline-block">
                            ✓ Archivo validado
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                          <p className="text-gray-600 font-medium">
                            {isIESDragActive
                              ? "Suelta el archivo aquí"
                              : "Arrastra tu archivo CSV o haz clic para seleccionar"}
                          </p>
                          <p className="text-gray-400 text-sm">
                            Formatos soportados: CSV
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Process Button */}
                {fileSummary && (
                  <div className="grid md:grid-cols-2 gap-6 mt-10">
                    <StatCard
                      title="Símbolos Master cargados"
                      value={fileSummary.masterSymbols}
                      subtitle="Fuente de referencia"
                      icon={Layers}
                      color="border-l-navy-500"
                      isAnimated={true}
                    />
                    <StatCard
                      title="Símbolos IES cargados"
                      value={fileSummary.iesSymbols}
                      subtitle="Glosario comparado"
                      icon={GitBranch}
                      color="border-l-petrol-500"
                    />
                  </div>
                )}

                {/* Process Button */}
                <div className="text-center">
                  <button
                    onClick={uploadFiles}
                    disabled={!files.master || !files.ies || isProcessing || results}
                    className={`group relative px-12 py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                      !files.master || !files.ies || isProcessing || results
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-navy-600 to-petrol-600 text-white hover:from-navy-700 hover:to-petrol-700 transform hover:scale-105 shadow-2xl hover:shadow-navy-500/25"
                    }`}
                  >
                    <span className="flex items-center justify-center">
                      <Sparkles className="w-6 h-6 mr-3" />
                      Iniciar Análisis
                      <Target className="w-6 h-6 ml-3" />
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Results Tab */}
        {activeTab === "results" && results && (
          <div key={animationKey} className="space-y-8 fade-in">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6 floating">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Resultados del Análisis
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto text-lg">
                Visualización completa de la similitud entre glosarios de
                dominios
              </p>
            </div>

            {/* Summary Counters - REDISEÑADO */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="text-sm font-semibold text-green-800">
                        Símbolos con Similitud
                      </h3>
                    </div>
                    <div className="text-2xl font-bold text-green-900">
                      {
                        Object.values(finalStates).filter(
                          (state) => state === "Similitud"
                        ).length
                      }
                    </div>
                    <div className="text-xs text-green-600 mt-1">
                      {(
                        (Object.values(finalStates).filter(
                          (state) => state === "Similitud"
                        ).length /
                          Math.max(Object.keys(finalStates).length, 1)) *
                        100
                      ).toFixed(0)}
                      % del total
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-green-700">
                      {(
                        (Object.values(finalStates).filter(
                          (state) => state === "Similitud"
                        ).length /
                          Math.max(Object.keys(finalStates).length, 1)) *
                        100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-yellow-100 rounded-xl p-6 border border-blue-200 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <h3 className="text-sm font-semibold text-yellow-800">
                        Símbolos con Diferencias
                      </h3>
                    </div>
                    <div className="text-2xl font-bold text-yellow-900">
                      {
                        Object.values(finalStates).filter(
                          (state) => state === "Diferencia"
                        ).length
                      }
                    </div>
                    <div className="text-xs text-yellow-600 mt-1">
                      {(
                        (Object.values(finalStates).filter(
                          (state) => state === "Diferencia"
                        ).length /
                          Math.max(Object.keys(finalStates).length, 1)) *
                        100
                      ).toFixed(0)}
                      % del total
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-yellow-200 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-yellow-700">
                      {(
                        (Object.values(finalStates).filter(
                          (state) => state === "Diferencia"
                        ).length /
                          Math.max(Object.keys(finalStates).length, 1)) *
                        100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border border-red-200 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <h3 className="text-sm font-semibold text-red-800">
                        Símbolos con Omisiones
                      </h3>
                    </div>
                    <div className="text-2xl font-bold text-red-900">
                      {
                        Object.values(finalStates).filter(
                          (state) => state === "Omisión"
                        ).length
                      }
                    </div>
                    <div className="text-xs text-red-600 mt-1">
                      {(
                        (Object.values(finalStates).filter(
                          (state) => state === "Omisión"
                        ).length /
                          Math.max(Object.keys(finalStates).length, 1)) *
                        100
                      ).toFixed(0)}
                      % del total
                    </div>
                  </div>
                  <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-red-700">
                      {(
                        (Object.values(finalStates).filter(
                          (state) => state === "Omisión"
                        ).length /
                          Math.max(Object.keys(finalStates).length, 1)) *
                        100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Export Button - MEJORADO - OCULTO TEMPORALMENTE */}
            <div className="hidden flex justify-center mb-6">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold rounded-lg shadow-md hover:from-emerald-700 hover:to-emerald-800 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <Download className="w-5 h-5 mr-2" />
                Descargar Análisis Completo (CSV)
              </button>
            </div>

            {/* Gráficos principales - Sistema Grid Estandarizado */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {/* Heatmap de Similitud */}
              <div className="flex flex-col bg-white rounded-lg p-4 shadow-md border border-gray-200 h-[515px]">
                <div className="flex-shrink-0 mb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3
                        className="text-lg font-semibold text-gray-900 truncate"
                        title="Matriz de Similitud LEL"
                      >
                        Matriz de Similitud LEL
                      </h3>
                      <p className="text-xs text-gray-600 mt-1 truncate">
                        Dimensiones: {results.heatmap_data.y.length} ×{" "}
                        {results.heatmap_data.x.length}
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center space-x-2 text-xs text-gray-500 ml-3">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-blue-100 rounded-full"></div>
                        <span className="whitespace-nowrap">Baja</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="whitespace-nowrap">Alta</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <MaterialHeatmapImproved
                    data={results.heatmap_data}
                    title=""
                  />
                </div>
              </div>

              {/* Material Bar Chart - Estados de Similitud */}
              <div className="flex flex-col bg-white rounded-lg p-4 shadow-md border border-gray-200 h-[510px]">
                <div className="flex-shrink-0 mb-3">
                  <h3
                    className="text-lg font-semibold text-gray-900 truncate"
                    title="Estados de Similitud por Símbolo"
                  >
                    Estados de Similitud por Símbolo
                  </h3>
                  <p className="text-xs text-gray-600 mt-1 truncate">
                    {Object.keys(results.bar_data).length} elementos analizados
                  </p>
                </div>
                <div className="flex-1 min-h-0">
                  <MaterialBarChartHorizontal
                    data={
                      // Usar datos reales si están disponibles, sino datos de prueba
                      results.bar_data &&
                      Object.keys(results.bar_data).length > 0
                        ? Object.entries(results.bar_data).flatMap(
                            ([state, items]) =>
                              items.map((item) => ({
                                name: `${item.name} (${state})`,
                                value: item.value,
                                state: state,
                              }))
                          )
                        : [
                            { name: "planificar proyecto", value: 0.75 },
                            {
                              name: "revisión proyecto institucional",
                              value: 0.84,
                            },
                            { name: "cronograma investigación", value: 0.98 },
                            { name: "proyecto investigación", value: 0.89 },
                            { name: "proyecto aprobado", value: 0.85 },
                            { name: "programa investigación", value: 0.82 },
                            { name: "investigador principal", value: 1.0 },
                            { name: "docente colaborador", value: 0.72 },
                          ]
                    }
                    title=""
                    colors={Object.values(results.colors || {})}
                  />
                </div>
              </div>
            </div>

            {/* Gráficos adicionales - Sistema Grid Estandarizado */}
            {statistics && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* plot_symbol_type_comparison */}
                <div className="flex flex-col bg-white rounded-lg p-4 shadow-md border border-gray-200 h-[420px]">
                  <div className="flex-shrink-0 mb-3">
                    <h3
                      className="text-lg font-semibold text-gray-900 truncate"
                      title="plot_symbol_type_comparison"
                    >
                      Comparación por tipos de símbolos
                    </h3>
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      {Object.keys(statistics.master_types || {}).length +
                        Object.keys(statistics.ies_types || {}).length}{" "}
                      tipos únicos
                    </p>
                  </div>
                  <div className="flex-1 min-h-0">
                    <MaterialComparisonChart
                      masterTypes={statistics.master_types || {}}
                      iesTypes={statistics.ies_types || {}}
                      plotData={statistics.plot_symbol_type_comparison}
                      title=""
                    />
                  </div>
                </div>

                {/* Distribución de Similitudes */}
                <div className="flex flex-col bg-white rounded-lg p-4 shadow-md border border-gray-200 h-[420px]">
                  <div className="flex-shrink-0 mb-3">
                    <h3
                      className="text-lg font-semibold text-gray-900 truncate"
                      title="Distribución de Similitudes"
                    >
                      Distribución de Similitudes
                    </h3>
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      Clasificación por umbrales según función
                      distribucion_similitudes
                    </p>
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      • Similitud Alta ≥70% • Diferencia Moderada 30-70% •
                      Omisión &lt;30%
                    </p>
                  </div>
                  <div className="flex-1 min-h-0">
                    <MaterialPieChart
                      data={(() => {
                        // USAR SIEMPRE datos del backend (función distribucion_similitudes del original)
                        if (statistics?.similarity_distribution) {
                          const distribution =
                            statistics.similarity_distribution;
                          const totalMetrics =
                            statistics.master_symbols *
                            statistics.ies_symbols *
                            9; // 7 métricas por comparación (como en el original)

                          return [
                            {
                              name: "Similitud Alta (≥70%)",
                              value: distribution["Similitud Alta (≥70%)"] || 0,
                              count: Math.round(
                                (distribution["Similitud Alta (≥70%)"] *
                                  totalMetrics) /
                                  100
                              ),
                              color: "#22c55e", // Verde
                            },
                            {
                              name: "Diferencia Moderada (30-69%)",
                              value:
                                distribution["Diferencia Moderada (30–69%)"] ||
                                0,
                              count: Math.round(
                                (distribution["Diferencia Moderada (30–69%)"] *
                                  totalMetrics) /
                                  100
                              ),
                              color: "#fbbf24", // Amarrillo
                            },
                            {
                              name: "Omisión (<30%)",
                              value: distribution["Omisión (<30%)"] || 0,
                              count: Math.round(
                                (distribution["Omisión (<30%)"] *
                                  totalMetrics) /
                                  100
                              ),
                              color: "#ef4444", // Rojo
                            },
                          ].filter((item) => item.value > 0);
                        }

                        // Fallback SOLO si no hay datos del backend - usando la misma lógica que distribucion_similitudes

                        // Extraer todas las métricas individuales como en el original
                        const flatSimilarities = results.heatmap_data.z.flat();
                        const total = flatSimilarities.length;

                        const alta = flatSimilarities.filter(
                          (sim) => sim >= 0.7
                        ).length;
                        const media = flatSimilarities.filter(
                          (sim) => sim >= 0.3 && sim < 0.7
                        ).length;
                        const baja = flatSimilarities.filter(
                          (sim) => sim < 0.3
                        ).length;

                        return [
                          {
                            name: "Similitud Alta (≥70%)",
                            value: parseFloat(
                              ((alta / total) * 100).toFixed(2)
                            ),
                            count: alta,
                            color: "#22c55e", // Verde - mismo color que el original
                          },
                          {
                            name: "Diferencia Moderada (30-69%)",
                            value: parseFloat(
                              ((media / total) * 100).toFixed(2)
                            ),
                            count: media,
                            color: "#fbbf24", // Amarillo - mismo color que el original
                          },
                          {
                            name: "Omisión (<30%)",
                            value: parseFloat(
                              ((baja / total) * 100).toFixed(2)
                            ),
                            count: baja,
                            color: "#ef4444", // Rojo - mismo color que el original
                          },
                        ].filter((item) => item.count > 0);
                      })()}
                      title=""
                      colors={["#fbbf24", "#ef4444", "#22c55e"]} // Amarillo, Rojo, Verde - colores exactos de distribucion_similitudes
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analysis Tab - REDISEÑADO */}
        {activeTab === "analysis" && topMatches && (
          <div key={animationKey} className="space-y-6 fade-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6 floating">
                <Table className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Análisis Detallado por Términos
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto text-lg">
                Exploración detallada de matches y componentes de similitud
              </p>
              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleGeneratePdf}
                  disabled={loadingPdf || !topMatches || topMatches.length === 0}
                  className="inline-flex items-center px-6 py-3 bg-white text-navy-700 font-semibold rounded-lg shadow-md hover:bg-gray-50 hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loadingPdf ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Generando PDF...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Descargar Reporte PDF
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Controls - MEJORADO */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Filtrar por Estado
                  </label>
                  <select
                    value={filterState}
                    onChange={(e) => setFilterState(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="Similitud">✅ Similitud</option>
                    <option value="Diferencia">⚠️ Diferencia</option>
                    <option value="Omisión">❌ Omisión</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ordenar por
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="similarity">📊 Similitud</option>
                    <option value="ies_term">🏛️ Término IES</option>
                    <option value="master_term">📋 Término Maestro</option>
                    <option value="status">🏷️ Estado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orden
                  </label>
                  <button
                    onClick={() =>
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500 flex items-center justify-center"
                  >
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    {sortOrder === "asc" ? "Ascendente" : "Descendente"}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Buscar
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar términos..."
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Top Matches Table */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 card-shadow border border-white/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Table className="w-8 h-8 mr-3 text-navy-600" />
                  Mejor Coincidencia por Término IES
                </h3>
                <div className="text-sm text-gray-600">
                  Mostrando {getFilteredTopMatches().length} de{" "}
                  {topMatches.length} términos
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Término IES
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Mejor Coincidencia Maestro
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Puntaje
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredTopMatches().map((match, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {match.ies_term}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {match.master_term}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {(match.similarity * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStateColor(
                              match.status
                            )}`}
                          >
                            {match.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleTermClick(match.ies_term)}
                            className="text-navy-600 hover:text-navy-800 font-medium text-sm flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver Detalle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Lists Section */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Aligned Terms */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 card-shadow border border-white/20">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
                  Términos Alineados ({groupedAlignedTerms.length})
                </h3>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {groupedAlignedTerms.map((entry, index) => (
                    <div
                      key={index}
                      className="p-3 bg-green-50 rounded-lg border border-green-200"
                    >
                      <div className="text-sm font-medium text-green-800">
                        {entry.ies_term}
                      </div>
                      {entry.master_terms.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {entry.master_terms.map((masterTerm, masterIndex) => (
                            <div
                              key={masterIndex}
                              className="text-xs text-green-600"
                            >
                              {"↔ " + masterTerm}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {groupedAlignedTerms.length === 0 && (
                    <p className="text-gray-500 text-center py-4">
                      No hay términos alineados
                    </p>
                  )}
                </div>
              </div>

              {/* Difference Terms */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 card-shadow border border-white/20">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <AlertCircle className="w-6 h-6 mr-3 text-yellow-600" />
                  Términos con Diferencias ({groupedDifferenceTerms.length})
                </h3>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {groupedDifferenceTerms.map((term, index) => (
                    <div
                      key={index}
                      className="p-3 bg-yellow-50 rounded-lg border border-yellow-200"
                    >
                      <div className="text-sm font-medium text-yellow-800">
                        {term}
                      </div>
                    </div>
                  ))}
                  {groupedDifferenceTerms.length === 0 && (
                    <p className="text-gray-500 text-center py-4">
                      No hay términos con diferencias
                    </p>
                  )}
                </div>
              </div>

              {/* Missing Terms - MEJORADO */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 card-shadow border border-white/20">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <XCircle className="w-6 h-6 mr-3 text-red-600" />
                  Términos Faltantes ({missingTerms.length})
                </h3>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {missingTerms.map((term, index) => {
                    const isMaster =
                      term.domain === "Master" ||
                      (term.master_term &&
                        term.master_term !== "N/A" &&
                        term.ies_term === "N/A");
                    const displayName = isMaster
                      ? term.master_term
                      : term.ies_term;
                    const domainLabel = isMaster ? "Master" : "IES";

                    return (
                      <div
                        key={index}
                        className="p-3 bg-red-50 rounded-lg border border-red-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 border border-red-200">
                                Dominio {domainLabel}
                              </span>
                              <span className="text-xs text-red-500">
                                {term.status}
                              </span>
                            </div>
                            <div className="text-sm font-medium text-red-800">
                              {displayName}
                            </div>
                            <div className="text-xs text-red-600 mt-1">
                              Similitud máxima: {formatScore(term.similarity)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {missingTerms.length === 0 && (
                    <p className="text-gray-500 text-center py-4">
                      ✅ No hay términos faltantes detectados
                    </p>
                  )}
                </div>
              </div>

              {/* Omission Symbols */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 card-shadow border border-white/20">
                {(() => {
                  const omissionTerms = Object.entries(finalStates)
                    .filter(([, state]) => state === "Omisión")
                    .map(([term]) => term);
                  return (
                    <>
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                        <XCircle className="w-6 h-6 mr-3 text-red-600" />
                        Símbolos con Omisiones ({omissionTerms.length})
                      </h3>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {omissionTerms.map((term, index) => (
                          <div
                            key={index}
                            className="p-3 bg-red-50 rounded-lg border border-red-200"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 border border-red-200">
                                    Dominio IES
                                  </span>
                                  <span className="text-xs text-red-500">
                                    Omisión
                                  </span>
                                </div>
                                <div className="text-sm font-medium text-red-800">
                                  {term}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {omissionTerms.length === 0 && (
                          <p className="text-gray-500 text-center py-4">
                            ✅ No hay símbolos con omisiones
                          </p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Statistics Tab - Enhanced */}
        {activeTab === "statistics" && statistics && (
          <div key={animationKey} className="space-y-8 fade-in">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6 floating">
                <Activity className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Estadísticas Avanzadas del Análisis
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto text-lg">
                Métricas detalladas y análisis profundo de similitud entre
                símbolos
              </p>
            </div>

            {/* Symbol Count Statistics */}
            <div className="grid md:grid-cols-3 gap-6">
              <StatCard
                title="Total de Símbolos"
                value={statistics.master_symbols + statistics.ies_symbols}
                subtitle="Símbolos combinados"
                icon={Database}
                color="border-l-indigo-500"
                isAnimated={true}
              />
              <StatCard
                title="Símbolos Dominio Maestro"
                value={statistics.master_symbols}
                subtitle="Glosario de referencia"
                icon={FileText}
                color="border-l-navy-500"
              />
              <StatCard
                title="Símbolos Dominio IES"
                value={statistics.ies_symbols}
                subtitle="Glosario a comparar"
                icon={Target}
                color="border-l-petrol-500"
              />
            </div>

            {/* Similarity Type Analysis */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 card-shadow border border-white/20">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <Layers className="w-8 h-8 mr-3 text-navy-600" />
                Análisis por Tipo de Similitud
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <StatCard
                  title="Similitud Léxica"
                  value={`${lexicalSimilarityPercentage.toFixed(2)}%`}
                  subtitle="TF-IDF (nombres, nociones, impactos)"
                  icon={BarChart2}
                  color="border-l-blue-500"
                />
                <StatCard
                  title="Similitud Sintáctica"
                  value={`${syntacticSimilarityPercentage.toFixed(2)}%`}
                  subtitle="N-gramas (patrones de caracteres)"
                  icon={GitBranch}
                  color="border-l-red-500"
                />
                <StatCard
                  title="Similitud Semántica"
                  value={`${semanticSimilarityPercentage.toFixed(2)}%`}
                  subtitle="Embeddings (contexto semántico)"
                  icon={Brain}
                  color="border-l-green-500"
                />
              </div>
            </div>

            {/* Component Analysis - EXACTO como sourceLEL.ipynb */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 card-shadow border border-white/20">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
                <Shuffle className="w-8 h-8 mr-3 text-petrol-600" />
                Análisis por Componentes
              </h3>
              {componentPercentages ? (
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-6 text-lg">
                      Léxica
                    </h4>
                    <div className="space-y-4">
                      <StatCard
                        title="Nombres"
                        value={`${
                          componentPercentages["Léxico (Nombre)"] || 0
                        }%`}
                        icon={FileText}
                        color="border-l-blue-500"
                      />
                      <StatCard
                        title="Nociones"
                        value={`${
                          componentPercentages["Léxico (Nociones)"] || 0
                        }%`}
                        icon={Layers}
                        color="border-l-blue-500"
                      />
                      <StatCard
                        title="Impactos"
                        value={`${
                          componentPercentages["Léxico (Impactos)"] || 0
                        }%`}
                        icon={Target}
                        color="border-l-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-6 text-lg">
                      Sintáctica
                    </h4>
                    <div className="space-y-4">
                      <StatCard
                        title="Nombres"
                        value={`${
                          componentPercentages["Sintáctico (Nombre)"] || 0
                        }%`}
                        icon={FileText}
                        color="border-l-red-500"
                      />
                      <StatCard
                        title="Nociones"
                        value={`${
                          componentPercentages["Sintáctico (Nociones)"] || 0
                        }%`}
                        icon={Layers}
                        color="border-l-red-500"
                      />
                      <StatCard
                        title="Impactos"
                        value={`${
                          componentPercentages["Sintáctico (Impactos)"] || 0
                        }%`}
                        icon={Target}
                        color="border-l-red-500"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-6 text-lg">
                      Semántica
                    </h4>
                    <div className="space-y-4">
                      <StatCard
                        title="Nombres"
                        value={`${
                          componentPercentages["Semántico (Nombre)"] || 0
                        }%`}
                        icon={FileText}
                        color="border-l-green-500"
                      />
                      <StatCard
                        title="Nociones"
                        value={`${
                          componentPercentages["Semántico (Nociones)"] || 0
                        }%`}
                        icon={Layers}
                        color="border-l-green-500"
                      />
                      <StatCard
                        title="Impactos"
                        value={`${
                          componentPercentages["Semántico (Impactos)"] || 0
                        }%`}
                        icon={Target}
                        color="border-l-green-500"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-8">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-6 text-lg">
                      Léxica
                    </h4>
                    <div className="space-y-4">
                      <StatCard
                        title="Nombres"
                        value={`${(
                          statistics?.avg_name_tfidf * 100 || 0
                        ).toFixed(2)}%`}
                        icon={FileText}
                        color="border-l-blue-500"
                      />
                      <StatCard
                        title="Nociones"
                        value={`${(
                          statistics?.avg_notions_tfidf * 100 || 0
                        ).toFixed(2)}%`}
                        icon={Layers}
                        color="border-l-blue-500"
                      />
                      <StatCard
                        title="Impactos"
                        value={`${(
                          statistics?.avg_impacts_tfidf * 100 || 0
                        ).toFixed(2)}%`}
                        icon={Target}
                        color="border-l-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-6 text-lg">
                      Sintáctica
                    </h4>
                    <div className="space-y-4">
                      <StatCard
                        title="Nombres"
                        value={`${(
                          statistics?.avg_name_ngram * 100 || 0
                        ).toFixed(2)}%`}
                        icon={FileText}
                        color="border-l-red-500"
                      />
                      <StatCard
                        title="Nociones"
                        value={`${(
                          statistics?.avg_notions_ngram * 100 || 0
                        ).toFixed(2)}%`}
                        icon={Layers}
                        color="border-l-red-500"
                      />
                      <StatCard
                        title="Impactos"
                        value={`${(
                          statistics?.avg_impacts_ngram * 100 || 0
                        ).toFixed(2)}%`}
                        icon={Target}
                        color="border-l-red-500"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-700 mb-6 text-lg">
                      Semántica
                    </h4>
                    <div className="space-y-4">
                      <StatCard
                        title="Nombres"
                        value={`${(
                          statistics?.avg_name_semantic * 100 || 0
                        ).toFixed(2)}%`}
                        icon={FileText}
                        color="border-l-green-500"
                      />
                      <StatCard
                        title="Nociones"
                        value={`${(
                          statistics?.avg_notions_semantic * 100 || 0
                        ).toFixed(2)}%`}
                        icon={Layers}
                        color="border-l-green-500"
                      />
                      <StatCard
                        title="Impactos"
                        value={`${(
                          statistics?.avg_impacts_semantic * 100 || 0
                        ).toFixed(2)}%`}
                        icon={Target}
                        color="border-l-green-500"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Comparison Results by Symbols */}
            <div className="grid md:grid-cols-3 gap-6">
              <StatCard
                title="Símbolos con Similitud"
                value={
                  Object.values(finalStates).filter(
                    (state) => state === "Similitud"
                  ).length
                }
                subtitle={`${(
                  (Object.values(finalStates).filter(
                    (state) => state === "Similitud"
                  ).length /
                    Math.max(statistics.ies_symbols, 1)) *
                  100
                ).toFixed(0)}% del total`}
                icon={CheckCircle}
                color="border-l-green-500"
                trend={`${(
                  (Object.values(finalStates).filter(
                    (state) => state === "Similitud"
                  ).length /
                    Math.max(statistics.ies_symbols, 1)) *
                  100
                ).toFixed(0)}% Coincidencia`}
              />
              <StatCard
                title="Símbolos con Diferencias"
                value={
                  Object.values(finalStates).filter(
                    (state) => state === "Diferencia"
                  ).length
                }
                subtitle={`${(
                  (Object.values(finalStates).filter(
                    (state) => state === "Diferencia"
                  ).length /
                    Math.max(statistics.ies_symbols, 1)) *
                  100
                ).toFixed(1)}%`}
                icon={AlertCircle}
                color="border-l-yellow-500"
              />
              <StatCard
                title="Símbolos con Omisiones"
                value={
                  Object.values(finalStates).filter(
                    (state) => state === "Omisión"
                  ).length
                }
                subtitle={`${(
                  (Object.values(finalStates).filter(
                    (state) => state === "Omisión"
                  ).length /
                    Math.max(statistics.ies_symbols, 1)) *
                  100
                ).toFixed(1)}%`}
                icon={XCircle}
                color="border-l-red-500"
              />
            </div>

            {/* Global Statistics */}
            <div className="grid md:grid-cols-3 gap-6">
              <StatCard
                title="Similitud Promedio"
                value={`${(statistics.average_similarity * 100).toFixed(1)}%`}
                icon={TrendingUp}
                color="border-l-navy-500"
                isAnimated={true}
              />
              <StatCard
                title="Similitud Máxima"
                value={`${(statistics.max_similarity * 100).toFixed(1)}%`}
                icon={Target}
                color="border-l-petrol-500"
                trend="Mejor coincidencia"
              />
              <StatCard
                title="Similitud Mínima"
                value={`${(statistics.min_similarity * 100).toFixed(1)}%`}
                icon={Eye}
                color="border-l-gray-500"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Metodología Aplicada */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 card-shadow border border-white/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Brain className="w-8 h-8 mr-3 text-navy-600" />
                  Metodología Aplicada
                </h3>
                <div className="space-y-4">
                  <div className="group relative p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 cursor-pointer transition-all hover:shadow-md">
                    <h5 className="font-semibold text-blue-800 flex items-center">
                      <Brain className="w-5 h-5 mr-2" />
                      Preprocesamiento léxico-semántico
                    </h5>
                    <div className="absolute left-0 top-full mt-2 w-80 p-4 bg-white rounded-xl shadow-2xl border-2 border-blue-300 z-[100] hidden group-hover:block group-active:block">
                      <ul className="text-blue-700 text-sm space-y-1">
                        <li>• Extracción de tipo, nombre, nociones e impactos del LEL</li>
                        <li>• Normalización (lemmatización, eliminación de stopwords, …)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="group relative p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200 cursor-pointer transition-all hover:shadow-md">
                    <h5 className="font-semibold text-green-800 flex items-center">
                      <Target className="w-5 h-5 mr-2" />
                      Representación vectorial semántica
                    </h5>
                    <div className="absolute left-0 top-full mt-2 w-80 p-4 bg-white rounded-xl shadow-2xl border-2 border-green-300 z-[100] hidden group-hover:block group-active:block">
                      <ul className="text-green-700 text-sm space-y-1">
                        <li>• Embeddings (word2vec, BERT, ...)</li>
                        <li>• Similitud semántica usando coseno</li>
                      </ul>
                    </div>
                  </div>
                  <div className="group relative p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200 cursor-pointer transition-all hover:shadow-md">
                    <h5 className="font-semibold text-purple-800 flex items-center">
                      <Shuffle className="w-5 h-5 mr-2" />
                      Detección de correspondencias
                    </h5>
                    <div className="absolute left-0 bottom-full mb-2 w-80 p-4 bg-white rounded-xl shadow-2xl border-2 border-purple-300 z-[100] hidden group-hover:block group-active:block">
                      <ul className="text-purple-700 text-sm space-y-1">
                        <li>• Método de Alineación de Glosarios LEL</li>
                        <li>• Puntaje global de similitud</li>
                        <li>• Clasificación: similares, diferentes u omitidos.</li>
                      </ul>
                    </div>
                  </div>
                  <div className="group relative p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200 cursor-pointer transition-all hover:shadow-md">
                    <h5 className="font-semibold text-indigo-800 flex items-center">
                      <BarChart3 className="w-5 h-5 mr-2" />
                      Generación de informe de alineación
                    </h5>
                    <div className="absolute left-0 bottom-full mb-2 w-80 p-4 bg-white rounded-xl shadow-2xl border-2 border-indigo-300 z-[100] hidden group-hover:block group-active:block">
                      <ul className="text-indigo-700 text-sm space-y-1">
                        <li>• Tabla o matriz de similitud</li>
                        <li>• Clasificación en [Similares, Diferentes, Omitidos]</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Umbrales de Clasificación */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 card-shadow border border-white/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Target className="w-8 h-8 mr-3 text-navy-600" />
                  Umbrales de Clasificación
                </h3>
                <div className="space-y-4">
                  <div className="group relative flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200 cursor-pointer transition-all hover:shadow-md">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-green-500 rounded-full mr-3"></div>
                      <span className="text-green-700 font-bold">
                        Similitud Alta
                      </span>
                    </div>
                    <span className="text-green-600 font-bold text-lg">
                      ≥{" "}
                      {currentProfileInfo
                        ? (
                            currentProfileInfo.thresholds.similarity * 100
                          ).toFixed(0)
                        : "65"}
                      %
                    </span>
                    <div className="absolute left-0 top-full mt-2 w-96 p-4 bg-white rounded-xl shadow-2xl border-2 border-green-300 z-[100] hidden group-hover:block group-active:block">
                      <p className="text-green-700 text-sm">
                        Indica una fuerte correspondencia lingüística y conceptual entre los términos, considerándose equivalentes y aceptándose automáticamente en la alineación.
                      </p>
                    </div>
                  </div>
                  <div className="group relative flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200 cursor-pointer transition-all hover:shadow-md">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full mr-3"></div>
                      <span className="text-yellow-700 font-bold">
                        Diferencia Moderada
                      </span>
                    </div>
                    <span className="text-yellow-600 font-bold text-lg">
                      {currentProfileInfo
                        ? (
                            currentProfileInfo.thresholds.difference * 100
                          ).toFixed(0)
                        : "30"}
                      % -{" "}
                      {currentProfileInfo
                        ? (
                            currentProfileInfo.thresholds.similarity * 100
                          ).toFixed(0)
                        : "65"}
                      %
                    </span>
                    <div className="absolute left-0 top-full mt-2 w-96 p-4 bg-white rounded-xl shadow-2xl border-2 border-yellow-300 z-[100] hidden group-hover:block group-active:block">
                      <p className="text-yellow-700 text-sm">
                        Refleja una similitud parcial o ambigua que requiere revisión contextual o validación antes de confirmar la alineación.
                      </p>
                    </div>
                  </div>
                  <div className="group relative flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border border-red-200 cursor-pointer transition-all hover:shadow-md">
                    <div className="flex items-center">
                      <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                      <span className="text-red-700 font-bold">Omisión</span>
                    </div>
                    <span className="text-red-600 font-bold text-lg">
                      {"< "}
                      {currentProfileInfo
                        ? (
                            currentProfileInfo.thresholds.difference * 100
                          ).toFixed(0)
                        : "30"}
                      %
                    </span>
                    <div className="absolute left-0 bottom-full mb-2 w-96 p-4 bg-white rounded-xl shadow-2xl border-2 border-red-300 z-[100] hidden group-hover:block group-active:block">
                      <p className="text-red-700 text-sm">
                        Señala una baja o nula relación entre los términos, descartándolos del proceso de alineación por falta de correspondencia semántica.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && termDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-7xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Detalle: {termDetail.ies_term}
                </h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">
                      Perfil usado:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {termDetail.profile_used}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Umbral Similitud:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {(
                        termDetail.profile_info.thresholds.similarity * 100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Umbral Diferencia:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {(
                        termDetail.profile_info.thresholds.difference * 100
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                </div>
              </div>

              {/* Layout con Radar a la izquierda y Estadísticas a la derecha */}
              <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Radar Chart Section - Izquierda */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Gráfico de Radar - Análisis de Similitud
                    {termDetail.comparisons.length > 0 && termDetail.comparisons[selectedComparisonIndex] && (
                      <span className="text-sm font-normal text-gray-600 ml-2 block mt-1">
                        (Comparando con: {termDetail.comparisons[selectedComparisonIndex].master_term})
                      </span>
                    )}
                  </h4>
                  <div className="bg-white rounded-lg p-4" style={{ height: '500px', position: 'relative' }}>
                    <RadarChart
                      termName={termDetail.ies_term}
                      similarities={
                        termDetail.comparisons.length > 0 && termDetail.comparisons[selectedComparisonIndex]
                          ? termDetail.comparisons[selectedComparisonIndex].components
                          : {
                              nombre_sim: 0,
                              nociones_tfidf_sim: 0,
                              impactos_tfidf_sim: 0,
                              nociones_ngram_sim: 0,
                              impactos_ngram_sim: 0,
                              nociones_emb_sim: 0,
                              impactos_emb_sim: 0,
                            }
                      }
                      status={
                        termDetail.comparisons.length > 0 && termDetail.comparisons[selectedComparisonIndex]
                          ? termDetail.comparisons[selectedComparisonIndex].status
                          : null
                      }
                    />
                  </div>
                </div>

                {/* Estadísticas Section - Derecha */}
                <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Estadísticas del Término
                  </h4>
                  {termDetail.comparisons.length > 0 && termDetail.comparisons[selectedComparisonIndex] ? (
                    <div className="space-y-4">
                      {/* Puntaje Total */}
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-600">Puntaje Total</span>
                          <span className={`text-2xl font-bold ${
                            termDetail.comparisons[selectedComparisonIndex].status === 'Similitud'
                              ? 'text-green-600'
                              : termDetail.comparisons[selectedComparisonIndex].status === 'Diferencia'
                              ? 'text-amber-600'
                              : 'text-red-600'
                          }`}>
                            {(termDetail.comparisons[selectedComparisonIndex].overall_similarity * 100).toFixed(1)}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full ${
                              termDetail.comparisons[selectedComparisonIndex].status === 'Similitud'
                                ? 'bg-green-600'
                                : termDetail.comparisons[selectedComparisonIndex].status === 'Diferencia'
                                ? 'bg-amber-600'
                                : 'bg-red-600'
                            }`}
                            style={{ width: `${termDetail.comparisons[selectedComparisonIndex].overall_similarity * 100}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Estado */}
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <span className="text-sm font-medium text-gray-600 block mb-2">Estado</span>
                        <span className={`inline-flex px-3 py-1.5 text-sm font-medium rounded-full ${
                          termDetail.comparisons[selectedComparisonIndex].status === 'Similitud'
                            ? 'bg-green-100 text-green-800'
                            : termDetail.comparisons[selectedComparisonIndex].status === 'Diferencia'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {termDetail.comparisons[selectedComparisonIndex].status}
                        </span>
                      </div>

                      {/* Métricas Detalladas */}
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Métricas Detalladas</h5>
                        <div className="space-y-3">
                          {/* Nombre */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-600">Nombre</span>
                              <span className="text-xs font-bold text-gray-900">
                                {(termDetail.comparisons[selectedComparisonIndex].components.nombre_sim * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${termDetail.comparisons[selectedComparisonIndex].components.nombre_sim * 100}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Nociones TF-IDF */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-600">Nociones TF-IDF</span>
                              <span className="text-xs font-bold text-gray-900">
                                {(termDetail.comparisons[selectedComparisonIndex].components.nociones_tfidf_sim * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-purple-500 h-2 rounded-full"
                                style={{ width: `${termDetail.comparisons[selectedComparisonIndex].components.nociones_tfidf_sim * 100}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Nociones N-gram */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-600">Nociones N-gram</span>
                              <span className="text-xs font-bold text-gray-900">
                                {(termDetail.comparisons[selectedComparisonIndex].components.nociones_ngram_sim * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-indigo-500 h-2 rounded-full"
                                style={{ width: `${termDetail.comparisons[selectedComparisonIndex].components.nociones_ngram_sim * 100}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Nociones Embeddings */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-600">Nociones Embeddings</span>
                              <span className="text-xs font-bold text-gray-900">
                                {(termDetail.comparisons[selectedComparisonIndex].components.nociones_emb_sim * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-cyan-500 h-2 rounded-full"
                                style={{ width: `${termDetail.comparisons[selectedComparisonIndex].components.nociones_emb_sim * 100}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Impactos TF-IDF */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-600">Impactos TF-IDF</span>
                              <span className="text-xs font-bold text-gray-900">
                                {(termDetail.comparisons[selectedComparisonIndex].components.impactos_tfidf_sim * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-pink-500 h-2 rounded-full"
                                style={{ width: `${termDetail.comparisons[selectedComparisonIndex].components.impactos_tfidf_sim * 100}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Impactos N-gram */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-600">Impactos N-gram</span>
                              <span className="text-xs font-bold text-gray-900">
                                {(termDetail.comparisons[selectedComparisonIndex].components.impactos_ngram_sim * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-orange-500 h-2 rounded-full"
                                style={{ width: `${termDetail.comparisons[selectedComparisonIndex].components.impactos_ngram_sim * 100}%` }}
                              ></div>
                            </div>
                          </div>

                          {/* Impactos Embeddings */}
                          <div>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs font-medium text-gray-600">Impactos Embeddings</span>
                              <span className="text-xs font-bold text-gray-900">
                                {(termDetail.comparisons[selectedComparisonIndex].components.impactos_emb_sim * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-teal-500 h-2 rounded-full"
                                style={{ width: `${termDetail.comparisons[selectedComparisonIndex].components.impactos_emb_sim * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      No hay datos disponibles
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> Haz clic en cualquier fila de la tabla para actualizar el gráfico de radar con esa comparación específica.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Término Maestro
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Puntaje Total
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Nombre
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Nociones TF-IDF
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Nociones N-gram
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Nociones Embeddings
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Impactos TF-IDF
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Impactos N-gram
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        Impactos Embeddings
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {termDetail.comparisons.slice(0, 10).map((comp, index) => (
                      <tr
                        key={index}
                        onClick={() => setSelectedComparisonIndex(index)}
                        className={`border-b border-gray-100 cursor-pointer transition-all ${
                          selectedComparisonIndex === index
                            ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-l-blue-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {comp.master_term}
                          {selectedComparisonIndex === index && (
                            <span className="ml-2 text-blue-600 text-xs">● Seleccionado</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(comp.overall_similarity * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStateColor(
                              comp.status
                            )}`}
                          >
                            {comp.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {(comp.components.nombre_sim * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-3">
                          {(comp.components.nociones_tfidf_sim * 100).toFixed(
                            1
                          )}
                          %
                        </td>
                        <td className="px-4 py-3">
                          {(comp.components.nociones_ngram_sim * 100).toFixed(
                            1
                          )}
                          %
                        </td>
                        <td className="px-4 py-3">
                          {(comp.components.nociones_emb_sim * 100).toFixed(1)}%
                        </td>
                        <td className="px-4 py-3">
                          {(comp.components.impactos_tfidf_sim * 100).toFixed(
                            1
                          )}
                          %
                        </td>
                        <td className="px-4 py-3">
                          {(comp.components.impactos_ngram_sim * 100).toFixed(
                            1
                          )}
                          %
                        </td>
                        <td className="px-4 py-3">
                          {(comp.components.impactos_emb_sim * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footerr */}
      <footer className="bg-white/5 backdrop-blur-sm border-t border-white/10 py-4 relative z-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-white/60 text-sm">
            <span className="group relative inline-block cursor-help">
              <span className="hover:text-white/80 transition-colors">
                Sistema de evaluación de correspondencias entre glosarios LEL
              </span>
              <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-[400px] p-4 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-2xl border-2 border-purple-200 z-[100] hidden group-hover:block">
                <div className="text-gray-800 text-sm space-y-2">
                  <div className="flex items-center">
                    <span className="font-semibold text-purple-700 min-w-[90px]">Método:</span>
                    <span className="text-gray-700 flex items-center flex-nowrap whitespace-nowrap">
                    Paola Grijalva Arriaga (UNLP
                      <img src="https://flagcdn.com/16x12/ar.png" alt="Argentina" className="inline-block h-3 mx-1 align-middle" />
                      - UAE
                      <img src="https://flagcdn.com/16x12/ec.png" alt="Ecuador" className="inline-block h-3 mx-1 align-middle" />
                      )
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-purple-700 min-w-[90px]">Back-end:</span>
                    <span className="text-gray-700 flex items-center flex-nowrap whitespace-nowrap">
                      Galo Cornejo Gomez (UCSG
                      <img src="https://flagcdn.com/16x12/ec.png" alt="Ecuador" className="inline-block h-3 mx-1 align-middle" />
                      )
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-purple-700 min-w-[90px]">Front-end:</span>
                    <span className="text-gray-700 flex items-center flex-nowrap whitespace-nowrap">
                      Moises Troya Cobeña (UCSG
                      <img src="https://flagcdn.com/16x12/ec.png" alt="Ecuador" className="inline-block h-3 mx-1 align-middle" />
                      )
                    </span>
                  </div>
                </div>
              </span>
            </span>
            {' • '}{new Date().getFullYear()}
          </p>
        </div>
      </footer>

      {/* Format Help Modal */}
      {showFormatModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-navy-600 to-petrol-600 text-white p-5 rounded-t-2xl flex-shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <h3 className="text-xl font-bold">Formato de Archivo CSV</h3>
                </div>
                <button
                  onClick={() => setShowFormatModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-lg transition-all flex-shrink-0"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-navy-600" />
                  Estructura del archivo
                </h4>
                <p className="text-gray-700 text-base mb-2">
                  El archivo CSV debe contener las siguientes columnas separadas por punto y coma (;):
                </p>
                <div className="bg-navy-50 border border-navy-200 rounded-lg p-3">
                  <code className="text-navy-800 font-mono text-sm font-semibold">
                    indice;tipo;nombre;nociones;impactos
                  </code>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Database className="w-5 h-5 text-navy-600" />
                  Descripción de columnas
                </h4>
                <div className="space-y-2">
                  <div className="bg-gray-50 p-2.5 rounded border border-gray-200">
                    <span className="font-semibold text-gray-900 text-sm">indice:</span>
                    <span className="text-gray-700 ml-2 text-sm">Número secuencial (1, 2, 3, ...)</span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded border border-gray-200">
                    <span className="font-semibold text-gray-900 text-sm">tipo:</span>
                    <span className="text-gray-700 ml-2 text-sm">Tipo de símbolo LEL (VERBO, SUJETO, OBJETO, ESTADO)</span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded border border-gray-200">
                    <span className="font-semibold text-gray-900 text-sm">nombre:</span>
                    <span className="text-gray-700 ml-2 text-sm">Nombre del término</span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded border border-gray-200">
                    <span className="font-semibold text-gray-900 text-sm">nociones:</span>
                    <span className="text-gray-700 ml-2 text-sm">Descripción del término (usar // para separar múltiples nociones)</span>
                  </div>
                  <div className="bg-gray-50 p-2.5 rounded border border-gray-200">
                    <span className="font-semibold text-gray-900 text-sm">impactos:</span>
                    <span className="text-gray-700 ml-2 text-sm">Efectos o consecuencias (usar // para separar múltiples impactos)</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-navy-600" />
                  Ejemplo completo
                </h4>
                <div className="bg-gradient-to-br from-petrol-50 to-navy-50 border border-petrol-200 rounded-lg p-3 overflow-x-auto">
                  <code className="text-petrol-900 font-mono text-sm whitespace-pre-wrap break-all">
                    1;VERBO;Planificar proyectos;Crear cronogramas//Asignar recursos;Normas aprobadas//Evaluación de resultados
                  </code>
                </div>
                <div className="mt-3 text-sm text-gray-600 bg-yellow-50/50 border border-yellow-200 rounded-lg p-2.5">
                  <span className="font-semibold text-yellow-800">Nota:</span> Use <code className="bg-yellow-100 px-1 rounded text-xs">{'//'}  </code> para separar múltiples nociones o impactos dentro de la misma celda.
                </div>
              </div>

              <div className="bg-petrol-50 border border-petrol-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-petrol-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-petrol-900 text-base mb-1">Consejos</p>
                    <ul className="text-sm text-petrol-800 space-y-1 list-disc list-inside">
                      <li>Asegúrate de que el archivo tenga extensión .csv</li>
                      <li>Usa punto y coma (;) como separador de campos</li>
                      <li>No uses punto y coma dentro del contenido de los campos</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 p-3 rounded-b-2xl border-t border-gray-200">
              <button
                onClick={() => setShowFormatModal(false)}
                className="w-full bg-gradient-to-r from-navy-600 to-petrol-600 text-white py-2.5 px-4 rounded-lg font-semibold hover:from-navy-700 hover:to-petrol-700 transition-all duration-200 shadow-lg text-sm"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
